import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { NetworkLinkedDocument } from 'src/modules/enaio/netgen/NetworkLinkedDocument';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { NetworkLinkedDocumentCategory } from 'src/modules/enaio/netgen/NetworkLinkedDocumentCategory';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { Utils } from 'src/modules/utils/shared/utils';
import { NetworkLinkedDocumentShareType } from 'src/modules/enaio/netgen/NetworkLinkedDocumentShareType';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { Strings } from 'src/modules/utils/shared/strings';

@Component({
    selector: 'app-edit-network-linked-document',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditNetworkLinkedDocumentComponent extends AdminBaseEditComponent<NetworkLinkedDocument> {

    service = inject(ProjectPlanningService);

    definition = ProjectPlanningTools.definitionNetworkLinkedDocument;

    users: DatabaseUser[];
    categories: NetworkLinkedDocumentCategory[] = [];
        
    getForm(): FrontendForm {
        return this.app.tForm("entity.NetworkLinkedDocument.fields", new FrontendForm([
            new FrontendField("title", "", FrontendFieldType.string, { mandatory: true }),
            new FrontendField("filePath", "", FrontendFieldType.string, {
                mandatory: true,
                validator: field => this.isFilePathAllowed(field.getValueString()) ? [] : [new FrontendFieldError(field.id, this.app.t("entity.NetworkLinkedDocument.other.invalidFilePath"))]
            }),
            new FrontendField("categoryId", "", FrontendFieldType.dropdown, {
                listItems: FrontendFieldListItem.make(this.categories, item => [item.id, item.title], true, [[null, ""]]),
                dropdownCanFilter: true
            }),
            new FrontendField("shareType", "", FrontendFieldType.dropdown, {
                listItems: FrontendFieldListItem.make(Utils.getEnumValues(NetworkLinkedDocumentShareType), item => [item, NetworkLinkedDocumentShareType.getDisplayName(item, this.app)], false),
                onValueChanged: this.shareTypeChanged.bind(this),
                callValueChangedAfterLoad: true
            }),
            new FrontendField("forUsersIds", "", FrontendFieldType.dropdownMulti, { 
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true)
            }),
                                        
        ]));
    }

    async initAdditionalData(): Promise<void> {
        this.users = await this.service.getActiveUsers();
        this.categories = await this.service.getNetworkLinkedDocumentCategories();
        this.refreshCategoryField();
    }

    getDialogButtons(): ButtonDefinition[] {
        return [
            ...super.getDialogButtons(),
            !this.isInsert() ? new ButtonDefinition("delete", this.app.t("general.delete"), true, "fas fa-trash", "", "danger") : null
        ];
    }
    
    async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "delete") {
            if (!await this.app.dialog.yesNo(this.app.t("entity.NetworkLinkedDocument.other.deleteConfirmation"), this.app.t("entity.NetworkLinkedDocument.other.deleteTitle"))) {
                return;
            }
            await ProjectPlanningTools.definitionNetworkLinkedDocument.deleteDataHandler(this.item.id, this.app, this);
            await this.close();
        }
        else {
            await super.buttonClickedFromExtern(id);
        }
    }

    private refreshCategoryField(): void {
        const field = this.form?.getField("categoryId");
        if (field == null) {
            return;
        }
        field.listItems = FrontendFieldListItem.make(this.categories, item => [item.id, item.title], true, [[null, ""]]);
        const currentValue = this.form.getValue("categoryId");
        if (currentValue != null && !field.listItems.some(li => li.value == currentValue)) {
            field.setValue(null);
        }
    }

    private shareTypeChanged(): void {
        this.form.getField("forUsersIds").visible = this.form.getValue("shareType") == NetworkLinkedDocumentShareType.certain;
    }

    private isFilePathAllowed(filePath: string): boolean {
        if (Utils.isNoe(filePath) || Utils.isNoe(this.app.mainConfig.networkLinkedDocumentAllowedPathRegex)) {
            return false;
        }

        try {
            return Strings.regexMatches(filePath, this.app.mainConfig.networkLinkedDocumentAllowedPathRegex);
        }
        catch {
            return false;
        }
    }
}
